import React from 'react';

const ReferentProspectingTutorial: React.FC = () => {
  return (
    <div className="space-y-6 text-sm text-gray-700">
      <section className="rounded-xl border-l-4 border-blue-600 bg-gradient-to-br from-gray-50 to-gray-100 p-6 shadow-sm">
        <h3 className="mb-4 flex items-center gap-2 text-xl font-bold text-blue-600"><span role="img" aria-label="target">🎯</span> À quoi sert ce module ?</h3>
        <p>
          Ce module vous permet d'identifier et de qualifier les copropriétés de votre parc qui présentent le meilleur potentiel pour le boîtage commercial. Il vous aide à optimiser vos actions de prospection en ciblant les copropriétés les plus rentables.
        </p>

        <div className="my-4 rounded-xl border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 to-emerald-100 p-5 shadow-sm">
          <h5 className="mb-2 text-lg font-bold text-emerald-700"><span role="img" aria-label="target">🎯</span> CAS N°1 : Identification des copropriétés prioritaires</h5>
          <p>Vous voulez identifier les copropriétés où concentrer vos efforts de prospection. Grâce aux filtres, vous pouvez cibler les copropriétés avec un fort volume de ventes, un CA élevé ou un potentiel de commission important.</p>
          <p className="font-semibold">Comment procéder :</p>
          <ul className="ml-6 list-disc space-y-1">
            <li>Utilisez les filtres "Nombre de ventes", "Montant du CA" et "Commissions" dans le bandeau bleu</li>
            <li>Cliquez sur "Qualification pour Boîtage" pour appliquer des critères automatiques</li>
            <li>Analysez les données des 4 dernières années dans la section expandable</li>
            <li>Exportez la liste filtrée pour vos équipes commerciales</li>
          </ul>
        </div>

        <div className="my-4 rounded-xl border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 to-emerald-100 p-5 shadow-sm">
          <h5 className="mb-2 text-lg font-bold text-emerald-700"><span role="img" aria-label="chart">📊</span> CAS N°2 : Suivi et organisation du boîtage</h5>
          <p>Vous voulez organiser et suivre vos campagnes de boîtage de manière structurée. Le module vous permet de qualifier chaque copropriété et de planifier vos actions.</p>
          <p className="font-semibold">Les avantages :</p>
          <ul className="ml-6 list-disc space-y-1">
            <li>Qualification "Oui/Non/Pas qualifié" pour chaque copropriété</li>
            <li>Suivi des dates de boîtage réalisé</li>
            <li>Génération de rapports personnalisés pour vos prospects</li>
            <li>Accès aux données des SCI présentes dans chaque copropriété</li>
          </ul>
        </div>
      </section>

      <section className="rounded-xl border-l-4 border-blue-600 bg-gradient-to-br from-gray-50 to-gray-100 p-6 shadow-sm">
        <h3 className="mb-4 flex items-center gap-2 text-xl font-bold text-blue-600"><span role="img" aria-label="check mark">✅</span> À FAIRE : Qualification et ciblage de votre portefeuille</h3>

        <h4 className="mt-1 mb-2 text-lg font-semibold text-indigo-700">A - Identifier vos copropriétés prioritaires</h4>
        <div className="rounded-xl border-l-4 border-blue-400 bg-gradient-to-br from-blue-50 to-blue-100 p-5 font-medium text-blue-800 shadow-sm">
          Utilisez les filtres intelligents pour identifier rapidement les copropriétés à fort potentiel. Les critères recommandés : plus de x ventes, CA supérieur à x €, ou commission potentielle élevée.
        </div>

        <h4 className="mt-4 mb-2 text-lg font-semibold text-indigo-700">B - Qualifier pour le boîtage</h4>
        <div className="rounded-xl border-l-4 border-blue-400 bg-gradient-to-br from-blue-50 to-blue-100 p-5 font-medium text-blue-800 shadow-sm">
          Pour chaque copropriété identifiée, utilisez le bouton "Qualification copro pour boîtage" pour définir si elle mérite d'être prospectée.
        </div>

        <p className="mt-2 font-semibold">Critères de qualification recommandés :</p>
        <ul className="ml-6 list-disc space-y-1">
          <li><strong>OUI</strong> : Copropriétés avec fort potentiel (volume de ventes élevé, prix attractifs, SCI nombreuses)</li>
          <li><strong>NON</strong> : Copropriétés avec peu d'activité ou prix trop faibles</li>
          <li><strong>PAS QUALIFIÉ</strong> : Copropriétés nécessitant une analyse complémentaire</li>
        </ul>

        <h4 className="mt-4 mb-2 text-lg font-semibold text-indigo-700">C - Organiser vos campagnes de boîtage</h4>
        <div className="rounded-xl border-l-4 border-blue-400 bg-gradient-to-br from-blue-50 to-blue-100 p-5 font-medium text-blue-800 shadow-sm">
          Une fois vos copropriétés qualifiées, planifiez et suivez vos actions terrain avec la colonne "Boîtage fait".
        </div>

        <p className="mt-2 font-semibold">Organisation recommandée :</p>
        <ul className="ml-6 list-disc space-y-1">
          <li>Filtrez sur "Qualifié = Oui" pour obtenir votre liste de prospection</li>
          <li>Planifiez vos tournées par secteur géographique</li>
          <li>Renseignez la date de boîtage après chaque passage</li>
          <li>Utilisez les rapports pour préparer vos arguments de vente</li>
          <li>Suivez les SCI présentes pour un ciblage plus précis</li>
        </ul>
      </section>

      <section className="rounded-xl border-l-4 border-blue-600 bg-gradient-to-br from-gray-50 to-gray-100 p-6 shadow-sm">
        <h3 className="mb-4 flex items-center gap-2 text-xl font-bold text-blue-600"><span role="img" aria-label="magnifying glass">🔍</span> Fonctionnalités principales</h3>

        <h4 className="mt-2 text-lg font-semibold text-indigo-700"><span role="img" aria-label="emoji">🎛</span>️ Filtres de ciblage avancés</h4>
        <ul className="ml-6 list-disc space-y-1">
          <li><strong>Nombre de ventes :</strong> Ciblez les copropriétés les plus actives</li>
          <li><strong>Montant du CA :</strong> Identifiez le potentiel économique</li>
          <li><strong>Commissions :</strong> Évaluez le retour sur investissement de vos actions</li>
          <li><strong>Qualification automatique :</strong> Algorithme de scoring intégré</li>
        </ul>

        <h4 className="mt-3 text-lg font-semibold text-indigo-700"><span role="img" aria-label="emoji">📈</span> Analyse historique sur 10 ans</h4>
        <ul className="ml-6 list-disc space-y-1">
          <li><strong>Évolution des ventes :</strong> Tendances du marché local</li>
          <li><strong>Suivi des DPE :</strong> Indicateur d'activité et de compliance</li>
          <li><strong>Calcul des commissions :</strong> ROI personnalisable selon votre taux</li>
        </ul>

        <h4 className="mt-3 text-lg font-semibold text-indigo-700"><span role="img" aria-label="target">🎯</span> Actions commerciales intégrées</h4>
        <ul className="ml-6 list-disc space-y-1">
          <li><strong>Rapports personnalisés :</strong> Arguments de vente sur-mesure</li>
          <li><strong>Avis de valeur :</strong> Service à valeur ajoutée</li>
          <li><strong>Suivi des SCI :</strong> Ciblage investors professionnels</li>
        </ul>
      </section>
    </div>
  );
};

export default ReferentProspectingTutorial;


